import { useRef } from 'react';

import { RouteManager } from '../../RouteManager';
import { ToastManager } from '../../ToastManager';
import { ContextMenuContent } from '../common/context-menu/ContextMenuContent';
import { DeveloperMenu } from '../common/context-menu/DeveloperMenu';

import { userMeDraftStore } from '../../store/users/UserMeDraftStore.ts';

import { useI18n } from '../../hooks/useI18nFormatters';
import { useAdvancedFlowsByCardOwnerUri } from '../../store/advanced-flow/useAdvancedFlows.ts';
import { useFlowsByCardOwnerUri } from '../../store/flow/useFlows.ts';
import { useMood } from '../../store/moods/useMoods.ts';
import { useApi } from '../../store/SelectedHomeyStore.hooks.ts';
import { useUserMe } from '../../store/users/useUserMe.ts';

import { glyph } from '../../theme/glyph';
import { theme } from '../../theme/theme';

export function MoodContextMenuContent(props) {
  const { i18n } = useI18n();
  // const ctx = useContextMenuContext();

  const pendingRef = useRef({
    favorite: false,
  });
  const { api } = useApi();
  const { mood } = useMood({ moodId: props.moodId });
  const { userMe } = useUserMe();

  const flows = useFlowsByCardOwnerUri();
  const advancedFlows = useAdvancedFlowsByCardOwnerUri();
  const moodFlows = flows.byCardOwnerUri?.[`homey:mood:${props.moodId}`] ?? {};
  const moodAdvancedFlows = advancedFlows.byCardOwnerUri?.[`homey:mood:${props.moodId}`] ?? {};
  const moodFlowsAndAdvancedFlows = Object.values({ ...moodFlows, ...moodAdvancedFlows });

  const isFavoriteMood = userMe?.properties.favoriteMoods?.includes(props.moodId) ?? false;

  async function onAction(key) {
    switch (key) {
      case 'set':
        api.moods
          .setMood({ id: props.moodId })
          .then(() => {})
          .catch(ToastManager.handleError);
        break;
      case 'favorite':
        (async () => {
          const favoriteName = `**${mood.name}**`;

          if (pendingRef.current.favorite === true) {
            ToastManager.add({
              message: 'Request still pending, please wait...',
            });
            return;
          }

          if (isFavoriteMood) {
            try {
              pendingRef.current.favorite = true;

              const nextUserMe = {
                ...userMe,
                properties: {
                  ...userMe.properties,
                  favoriteMoods: (userMe.properties.favoriteMoods ?? []).filter(
                    (id) => id !== props.moodId,
                  ),
                },
              };
              userMeDraftStore.setUserMeDraft({ userMe: nextUserMe });

              await api.users.updateUserMeProperties({
                id: 'favoriteMoods',
                value: nextUserMe.properties.favoriteMoods,
              });

              ToastManager.add({
                icon: glyph.core.heartOutline,
                iconColor: theme.icon.color_light,
                message: i18n.messageFormatter(`mood.removeFavorite`, {
                  name: favoriteName,
                }),
              });
              userMeDraftStore.setUserMeDraftDone();
            } catch (error) {
              userMeDraftStore.setUserMeDraftFailed();
              ToastManager.handleError(error);
            } finally {
              pendingRef.current.favorite = false;
            }

            return;
          }

          try {
            pendingRef.current.favorite = true;

            const nextUserMe = {
              ...userMe,
              properties: {
                ...userMe.properties,
                favoriteMoods: [...(userMe.properties.favoriteMoods ?? []), props.moodId],
              },
            };
            userMeDraftStore.setUserMeDraft({ userMe: nextUserMe });

            await api.users.updateUserMeProperties({
              id: 'favoriteMoods',
              value: nextUserMe.properties.favoriteMoods,
            });

            ToastManager.add({
              icon: glyph.core.heartFill,
              iconColor: theme.color.special_favorite,
              message: i18n.messageFormatter(`mood.addFavorite`, {
                name: favoriteName,
              }),
            });
            userMeDraftStore.setUserMeDraftDone();
          } catch (error) {
            userMeDraftStore.setUserMeDraftFailed();
            ToastManager.handleError(error);
          } finally {
            pendingRef.current.favorite = false;
          }
        })().catch(ToastManager.handleError);
        break;
      case 'flows':
        RouteManager.toFlows({
          moodId: props.moodId,
        });
        break;
      case 'delete':
        api.moods
          .deleteMood({ id: props.moodId })
          .then(() => {})
          .catch(ToastManager.handleError);
        break;
      default:
        break;
    }
  }

  return (
    <ContextMenuContent.List>
      <ContextMenuContent.ListItem
        label={i18n.messageFormatter(`mood.set`)}
        icon={glyph.core.mouseButtonLeft}
        onPress={() => {
          onAction('set');
        }}
      />

      <ContextMenuContent.ListItem
        label={i18n.messageFormatter(`mood.favorite`)}
        icon={isFavoriteMood ? glyph.core.heartFill : glyph.core.heartOutline}
        onPress={() => {
          onAction('favorite');
        }}
      />

      <DeveloperMenu onIdRequest={() => mood?.id} onResourceRequest={() => mood} />

      <ContextMenuContent.Divider />

      <ContextMenuContent.ListItem
        hint={moodFlowsAndAdvancedFlows.length}
        label="Flows"
        onPress={() => {
          onAction('flows');
        }}
      />

      <ContextMenuContent.Divider />

      <ContextMenuContent.ListItem
        label={i18n.messageFormatter('common.delete')}
        variant="danger"
        icon={glyph.core.trashFill}
        onPress={() => {
          onAction('delete');
        }}
      />
    </ContextMenuContent.List>
  );
}
